/**
 * Decimal Delta Feed – Event Status (Appendix 1).
 * Used to avoid hardcoded numbers when checking match state (scheduled vs in-play, finished, etc.).
 */
export const EVENT_STATUS = {
  MATCH_SCHEDULED: 0,
  MATCH_STABLE: 1,
  BALL_IN_PROGRESS: 2,
  UNKNOWN_EVENT: 3,
  RUNS_LIKELY: 4,
  POSSIBLE_WICKET: 5,
  UMPIRE_REVIEW: 6,
  BREAK_IN_PLAY: 7,
  DATA_ASSEMBLY_ISSUE: 8,
  SUPER_OVER: 9,
  MATCH_COMPLETE: 10,
  EVENT_CLOSED: 11,
  EJECTED: 13,
} as const;

export type EventStatusId = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];

/** Statuses that mean the match is in play (not scheduled, not finished). */
export const IN_PLAY_STATUS_IDS = [
  EVENT_STATUS.MATCH_STABLE,
  EVENT_STATUS.BALL_IN_PROGRESS,
  EVENT_STATUS.UNKNOWN_EVENT,
  EVENT_STATUS.RUNS_LIKELY,
  EVENT_STATUS.POSSIBLE_WICKET,
  EVENT_STATUS.UMPIRE_REVIEW,
  EVENT_STATUS.BREAK_IN_PLAY,
  EVENT_STATUS.DATA_ASSEMBLY_ISSUE,
  EVENT_STATUS.SUPER_OVER,
] as const;

/** Statuses that mean the match has finished (no trading). */
export const FINISHED_STATUS_IDS = [
  EVENT_STATUS.MATCH_COMPLETE,
  EVENT_STATUS.EVENT_CLOSED,
  EVENT_STATUS.EJECTED,
] as const;

/** Normalize API value to number for comparison (API may send number or string). */
export function eventStatusToNumber(status: unknown): number | null {
  if (status == null) return null;
  if (typeof status === "number" && Number.isFinite(status)) return status;
  const n = parseInt(String(status), 10);
  return Number.isFinite(n) ? n : null;
}

/** String event_status from API may use spaces or hyphens; align to underscore keys. */
export function eventStatusStringKey(status: unknown): string {
  return String(status ?? "")
    .toUpperCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .trim();
}

export function isMatchScheduledStatus(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (n === null) return false;
  if (n === EVENT_STATUS.MATCH_SCHEDULED) return true;
  return eventStatusStringKey(status) === "MATCH_SCHEDULED";
}

export function isMatchStableStatus(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (n === EVENT_STATUS.MATCH_STABLE) return true;
  return eventStatusStringKey(status) === "MATCH_STABLE";
}

export function isBallInProgressStatus(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (n === EVENT_STATUS.BALL_IN_PROGRESS) return true;
  return eventStatusStringKey(status) === "BALL_IN_PROGRESS";
}

export function isBallInProgressOrMatchStable(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (n !== null && (n === EVENT_STATUS.MATCH_STABLE || n === EVENT_STATUS.BALL_IN_PROGRESS))
    return true;
  const s = eventStatusStringKey(status);
  return s === "MATCH_STABLE" || s === "BALL_IN_PROGRESS";
}

export function isBreakInPlayStatus(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (n === EVENT_STATUS.BREAK_IN_PLAY) return true;
  return eventStatusStringKey(status) === "BREAK_IN_PLAY";
}

/**
 * Decimal often drops `event_status` back to MATCH_SCHEDULED (0) or omits it between innings.
 * Infer the break from the scoreboard unless a ball is in progress, super over, or the match is over.
 */
export function cricketEventStatusAllowsInningsBreakInference(status: unknown): boolean {
  if (isBallInProgressStatus(status)) return false;
  if (isMatchFinishedStatus(status)) return false;
  const n = eventStatusToNumber(status);
  if (n === EVENT_STATUS.SUPER_OVER) return false;
  if (
    n === EVENT_STATUS.RUNS_LIKELY ||
    n === EVENT_STATUS.POSSIBLE_WICKET ||
    n === EVENT_STATUS.UMPIRE_REVIEW
  ) {
    return false;
  }
  return true;
}

/**
 * Delta feed `global_betting_status` / match_update.`betting_status`: open vs suspended.
 * Missing or unknown → treat as open so the lobby does not block play before the first update.
 */
export function isGlobalMatchTradingActive(tradingStatus: unknown): boolean {
  if (tradingStatus == null || tradingStatus === "") return true;
  if (tradingStatus === 0 || tradingStatus === "0") return false;
  if (tradingStatus === 1 || tradingStatus === "1") return true;
  const u = String(tradingStatus).toUpperCase();
  if (u === "SUSPENDED" || u === "INACTIVE") return false;
  if (u === "ACTIVE") return true;
  return true;
}

export function isMatchFinishedStatus(status: unknown): boolean {
  const n = eventStatusToNumber(status);
  if (
    n !== null &&
    (n === EVENT_STATUS.MATCH_COMPLETE ||
      n === EVENT_STATUS.EVENT_CLOSED ||
      n === EVENT_STATUS.EJECTED)
  )
    return true;
  const u = String(status ?? "").toUpperCase().replace(/-/g, "_").trim();
  if (!u || u.includes("NOT FINISHED") || u.includes("NOT ABANDONED")) return false;
  return (
    u.includes("EVENT CLOSED") ||
    u.includes("EVENT_CLOSED") ||
    u.includes("MATCH COMPLETE") ||
    u.includes("MATCH_COMPLETE") ||
    u.includes("FINISHED") ||
    u === "EVENT_FULL_RESULT" ||
    u.includes("ABANDON") ||
    u.includes("NO RESULT") ||
    u.includes("NO_RESULT")
  );
}

/**
 * True when Decimal scoreboard `winner` is a real team name.
 * Feed sentinels: `NA` (no winner yet / tie path) and `VOID` (abandoned / no result).
 */
export function isDecimalTeamWinner(winner: unknown): boolean {
  const w = String(winner ?? "").trim();
  if (!w) return false;
  const u = w.toUpperCase();
  return u !== "NA" && u !== "VOID";
}

/**
 * True when commentary looks like a match result (e.g. "won by 5 wickets", "India wins").
 * Ignores "won/wins the toss" so early-innings toss lines do not close the markets UI.
 * Also ignores the bogus "VOID wins." line produced from the VOID winner sentinel.
 */
export function commentIndicatesCricketMatchResult(comment: unknown): boolean {
  let c = String(comment ?? "").trim();
  if (!c) return false;
  if (/^void\s+wins\.?$/i.test(c)) return false;
  c = c.replace(/\b(won|wins)\s+the\s+toss\b/gi, " ").replace(/\s+/g, " ").trim();
  if (!c) return false;
  return /\bwins\s*$/i.test(c) || /\bwon\b/i.test(c) || /\bwins\b/i.test(c);
}

/** Chase line from Decimal scoreboard, e.g. "Romania require 3 runs from 29 balls." */
export function isCricketRrrComment(comment: unknown): boolean {
  return / require \d+ runs from \d+ ball/i.test(String(comment ?? "").trim());
}

/** Runs from a scorecard innings block, or null when the innings was not played. */
function cricketInningsRuns(innings: unknown): number | null {
  if (!innings || typeof innings !== "object") return null;
  const runs = Number.parseInt(String((innings as { runs?: unknown }).runs ?? ""), 10);
  if (!Number.isFinite(runs) || runs <= 0) return null;
  return runs;
}

/**
 * True when a finished-match result sentence cannot be reconciled with the innings totals,
 * e.g. Decimal closing a chase as "won by 5 wickets" while the second innings is still short
 * of the target. Reduced-target chases can legitimately win below the first-innings score, so
 * innings with different max overs are left alone, as are multi-innings (Test) results.
 */
export function cricketResultContradictsScores(params: {
  resultText?: unknown;
  scorecard?: Record<string, unknown> | null;
  maxOvers?: unknown;
}): boolean {
  const text = String(params.resultText ?? "").trim();
  if (!text) return false;

  const scorecard = params.scorecard;
  if (!scorecard || typeof scorecard !== "object") return false;
  if (cricketInningsRuns(scorecard.inns3) != null || cricketInningsRuns(scorecard.inns4) != null) {
    return false;
  }

  const firstInningsRuns = cricketInningsRuns(scorecard.inns1);
  const secondInningsRuns = cricketInningsRuns(scorecard.inns2);
  if (firstInningsRuns == null || secondInningsRuns == null) return false;

  if (Array.isArray(params.maxOvers)) {
    const firstMax = Number.parseFloat(String(params.maxOvers[0] ?? ""));
    const secondMax = Number.parseFloat(String(params.maxOvers[1] ?? ""));
    if (Number.isFinite(firstMax) && Number.isFinite(secondMax) && firstMax !== secondMax) {
      return false;
    }
  }

  if (/\bwon by \d+ wickets?\b/i.test(text)) return secondInningsRuns <= firstInningsRuns;
  if (/\bwon by \d+ runs?\b/i.test(text)) return firstInningsRuns <= secondInningsRuns;
  return false;
}

/**
 * Result sentence to render for a match, or "" when the feed text should be withheld:
 * a stale chase line, or a result the innings totals contradict.
 */
export function cricketResultTextForDisplay(params: {
  matchComment?: unknown;
  matchFinished?: boolean;
  scorecard?: Record<string, unknown> | null;
  maxOvers?: unknown;
}): string {
  const text = String(params.matchComment ?? "").trim();
  if (!text || !params.matchFinished) return text;
  if (/^void\s+wins\.?$/i.test(text)) return "";
  if (isCricketRrrComment(text)) return "";
  if (
    cricketResultContradictsScores({
      resultText: text,
      scorecard: params.scorecard,
      maxOvers: params.maxOvers,
    })
  ) {
    return "";
  }
  return text;
}

/**
 * Pull a result sentence out of scorecard / game_phase status, e.g.
 * "Match Complete - Nottinghamshire have won by 7 runs" → "Nottinghamshire won by 7 runs".
 */
export function resultCommentFromEventStatus(eventStatus: unknown): string {
  const raw = String(eventStatus ?? "").trim();
  if (!raw) return "";
  const afterComplete = raw.match(/^Match Complete\s*[-–—]\s*(.+)$/i)?.[1]?.trim() ?? "";
  const candidate = afterComplete || raw;
  if (
    !commentIndicatesCricketMatchResult(candidate) &&
    !/\b(tied|draw|drawn|no result)\b/i.test(candidate)
  ) {
    return "";
  }
  return candidate.replace(/\bhave\s+won\b/gi, "won").replace(/\s+/g, " ").trim();
}

/**
 * True when game_phase / caption means the *match* is over.
 * Do not treat "Innings Complete" (between-innings break) as finished — bare
 * `includes("complete")` falsely matched that mid-match caption and closed Trader.
 */
export function isCricketGamePhaseFinished(gamePhase: unknown): boolean {
  const phaseLower = String(gamePhase ?? "").toLowerCase().trim();
  if (!phaseLower) return false;
  // Mid-match: first innings done, second not started (or similar breaks).
  if (/\binnings?\s+complete\b/.test(phaseLower)) return false;
  return (
    phaseLower.includes("match complete") ||
    phaseLower.includes("event closed") ||
    phaseLower === "closed"
  );
}

export function resolveCricketMatchFinished(params: {
  eventStatus?: unknown;
  gamePhase?: unknown;
  winner?: unknown;
  matchComment?: unknown;
}): boolean {
  if (isMatchFinishedStatus(params.eventStatus)) return true;
  if (isCricketGamePhaseFinished(params.gamePhase)) return true;
  const winner = String(params.winner ?? "").trim();
  if (winner && winner !== "NA") return true;
  if (commentIndicatesCricketMatchResult(params.matchComment)) return true;
  return false;
}

/** True when both primary innings lines are empty placeholders (0/0 or missing). */
export function cricketPrimaryScoresLookEmpty(scores: unknown): boolean {
  if (!Array.isArray(scores) || scores.length < 2) return true;
  return scores.slice(0, 2).every((s) => cricketPrimaryScoreLineEmpty(s));
}

/** True when a single `runs/wkts` display line is an empty placeholder. */
export function cricketPrimaryScoreLineEmpty(score: unknown): boolean {
  const v = String(score ?? "").trim();
  return !v || v === "0/0" || v === "-";
}

/** Runs from a `runs/wkts` display line, or null when it is not a real score. */
export function cricketScoreLineRuns(score: unknown): number | null {
  const v = String(score ?? "").trim();
  if (!v) return null;
  const slashIdx = v.indexOf("/");
  const runs = Number.parseInt(slashIdx >= 0 ? v.slice(0, slashIdx) : v, 10);
  return Number.isFinite(runs) ? runs : null;
}

/** Runs summed across every innings slot, or null when none parse. */
export function cricketScoreLinesTotalRuns(scores: unknown): number | null {
  if (!Array.isArray(scores)) return null;
  let total: number | null = null;
  for (const line of scores) {
    const runs = cricketScoreLineRuns(line);
    if (runs == null) continue;
    total = (total ?? 0) + runs;
  }
  return total;
}
